import React from 'react';
import { useTranslation } from 'react-i18next';
import { PredictionsTable } from '@/components/PredictionsTable';
import { MainSection } from '@/components/layout/MainSection';
import { useMatchDetailContext } from '@/layouts/MatchDetailLayout/context';
import predictionsStyles from '@/pages/MatchPredictionsPage.module.scss';
import { SectionContent } from '@/components/layout/SectionContent';
import { TournamentMatchesLink } from '@/components/TournamentMatchesLink';

const PredictionsTab: React.FC = () => {
  const { t } = useTranslation(['tournament']);
  const {
    tournament,
    tournamentId,
    match,
    predictions,
    predictionsLoading,
    predictionsError,
    predictionsLocked,
    predictionsLockTime,
    isArchived,
    openedOutsideTournament,
  } = useMatchDetailContext();

  if (predictionsLoading) {
    return null;
  }

  if (predictionsError) {
    return (
      <div className={predictionsStyles.error}>
        <div className={predictionsStyles.errorIcon}>{'\u274C'}</div>
        <p className={predictionsStyles.errorText}>{t('tournament:errorLoadingPredictions')}</p>
      </div>
    );
  }

  const lockTimeLabel = predictionsLocked && predictionsLockTime
    ? new Date(predictionsLockTime).toLocaleString(undefined, {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;

  return (
    <MainSection
      hideTitleOnDesktop
      title={tournament?.name || 'Tournament'}
      subtitle={t('tournament:predictions')}
      logoUrl={tournament?.league?.logo_url || tournament?.leagueLogo}
      logoAlt={tournament?.league?.name || tournament?.leagueName}
      headerRight={openedOutsideTournament ? <TournamentMatchesLink tournamentId={tournamentId} /> : undefined}
    >
      <SectionContent>
        {isArchived && (
          <div className={predictionsStyles.lockedNotice} role="status">
            <p className={predictionsStyles.lockedNoticeTitle}>
              {t('tournament:tournamentArchivedNotice')}
            </p>
          </div>
        )}
        {predictionsLocked && (
          <div className={predictionsStyles.lockedNotice} role="status">
            <p className={predictionsStyles.lockedNoticeTitle}>
              {t('tournament:predictionsHiddenUntilDeadline')}
            </p>
            {lockTimeLabel && (
              <p className={predictionsStyles.lockedNoticeMeta}>
                {t('tournament:predictionsVisibleAt', { time: lockTimeLabel })}
              </p>
            )}
          </div>
        )}
        <PredictionsTable
          noWrapper
          title={`${match.team1} vs ${match.team2}`}
          subtitle={t('tournament:matchPredictions')}
          match={{
            team1: match.team1,
            team2: match.team2,
            team1_short: match.team1_short,
            team2_short: match.team2_short,
            team1_logo_url: match.team1_logo_url,
            team2_logo_url: match.team2_logo_url,
            score_team1: match.score_team1,
            score_team2: match.score_team2,
            status: match.status,
            match_date: match.match_date,
          }}
          predictions={predictions}
        />
      </SectionContent>
    </MainSection>
  );
};

export default PredictionsTab;
